<template>
  <a-modal
    title="视频截取"
    :visible="visible"
    @cancel="close"
    width="722px"
    @ok="handleOK"
    :maskClosable="false"
    class="video-cropper-modal"
  >
    <video
      :src="src"
      ref="video"
      class="video-cropper-modal__video"
      @timeupdate="handleCurrent"
      @durationchange="handleDurationChange"
      @play="handlePlay"
      @pause="handlePause"
    />
    <div class="video-cropper-container">
      <div class="video-cropper-container__start">00:00.00</div>
      <div class="video-cropper-container__end">
        {{ formatDuration(totalDuration) }}
      </div>
      <el-slider
        class="slide-container__slider video-cropper-container__item"
        :min="0"
        :max="totalDuration"
        :step="0.01"
        v-model="current"
        :disabled="playing"
        :formatTooltip="formatDuration"
      />
      <div class="video-cropper-container__body video-cropper-container__item">
        <div
          class="track"
          :class="{ 'track--selected': dragging }"
          :style="trackStyle"
          @mousedown="handleMouseDown"
        >
          <div
            class="track__handle track__handle--start"
            @mousedown.stop="handleStartMouseDown"
          />
          <div
            class="track__handle track__handle--end"
            @mousedown.stop="handleEndMouseDown"
          />
        </div>
      </div>
    </div>

    <a-row type="flex" align="middle" gutter="8">
      <a-col>
        <a-input-number
          class="video-cropper-modal__input"
          v-model="displayStart"
          :step="0.001"
          :precision="3"
          :min="0"
          :max="totalDuration - 0.4"
          @blur="handleStartInputChange"
          @keydown.native.13="handleStartInputChange"
        />
      </a-col>
      <a-col>&ndash;</a-col>
      <a-col>
        <a-input-number
          class="video-cropper-modal__input"
          v-model="displayEnd"
          :step="0.001"
          :max="totalDuration"
          :min="0.4"
          :precision="3"
          @blur="handleEndInputChange"
          @keydown.native.13="handleEndInputChange"
        />
      </a-col>
      <a-col flex="1 0 100px">截取后时长: {{ formatDuration(duration) }}</a-col>
      <a-col>
        <a-button
          type="primary"
          class="video-cropper-modal__btn"
          @click="togglePlay"
        >
          {{ playing ? "暂停" : "播放" }}
        </a-button>
      </a-col>
      <a-col>
        <a-button class="video-cropper-modal__btn" @click="reset"
          >重置</a-button
        >
      </a-col>
    </a-row>
  </a-modal>
</template>
<script>
import { clamp, formatTimeInterval } from '../../utils';

export default {
  props: ['visible', 'src', 'value'],
  data() {
    return {
      current: 0,
      totalDuration: 10,
      start: 0,
      end: 0,
      playing: false,
      dragging: false,
      displayStart: 0,
      displayEnd: 0
    }
  },
  computed: {
    duration() {
      return this.end - this.start;
    },
    trackStyle() {
      return {
        left: this.start * 100 / this.totalDuration + '%',
        width: `calc(${this.duration * 100 / this.totalDuration + '%'} + 22px)`,
        marginLeft: '-11px',
        padding: 0
      };
    }
  },
  methods: {
    close() {
      this.$emit('update:visible', false);
    },
    handleCurrent(e) {
      if (this.playing) {
        if (this.prevCurrent <= this.end && e.target.currentTime >= this.end || e.target.currentTime >= this.totalDuration) {
          e.target.pause();
          setTimeout(() => {
            this.current = this.start;
          }, 0);
        } else {
          this.current = e.target.currentTime;
        }
      }
    },
    handleDurationChange(e) {
      this.totalDuration = e.target.duration;
    },
    togglePlay() {
      if (this.playing) {
        this.$refs.video.pause();
      } else {
        this.prevCurrent = this.current;
        this.$refs.video.play();
      }
    },
    handlePlay() {
      this.playing = true;
    },
    handlePause() {
      this.playing = false;
    },
    formatDuration(value) {
      const milliseconds = value * 1e3;
      return formatTimeInterval(milliseconds);
    },
    reset() {
      if (this.value) {
        this.start = this.value[0];
        this.end = this.value[1];
      } else {
        this.start = 0;
        this.end = this.totalDuration;
      }
    },
    handleMouseDown(e) {
      this.dragging = true;
      this.origPos = e.clientX;
      this.origStart = this.start;
      this.origEnd = this.end;
      document.body.classList.add('unselectable');
      document.addEventListener('mousemove', this.handleMouseMove);
      document.addEventListener('mouseup', this.handleMouseUp);
    },
    handleMouseMove(e) {
      if (this.dragging) {
        const delta = clamp(
          (e.clientX - this.origPos) / 672 * this.totalDuration,
          -this.origStart,
          this.totalDuration - this.origEnd
        );
        this.start = this.origStart + delta;
        this.end = this.origEnd + delta;
      }
    },
    handleMouseUp() {
      if (this.dragging) {
        this.dragging = false;
        document.removeEventListener('mousemove', this.handleMouseMove);
        document.removeEventListener('mouseup', this.handleMouseUp);
        setTimeout(() => {
          document.body.classList.remove('unselectable');
        }, 0);
      }
    },
    handleStartMouseDown(e) {
      this.startDragging = true;
      this.origPos = e.clientX;
      this.origStart = this.start;
      document.body.classList.add('unselectable');
      document.addEventListener('mousemove', this.handleStartMouseMove);
      document.addEventListener('mouseup', this.handleStartMouseUp);
    },
    handleStartMouseMove(e) {
      if (this.startDragging) {
        const delta = (e.clientX - this.origPos) / 672 * this.totalDuration;
        this.start = clamp(this.origStart + delta, 0, this.end - .4);
      }
    },
    handleStartMouseUp() {
      if (this.startDragging) {
        this.startDragging = false;
        document.removeEventListener('mousemove', this.handleStartMouseMove);
        document.removeEventListener('mouseup', this.handleStartMouseUp);
        setTimeout(() => {
          document.body.classList.remove('unselectable');
        }, 0);
      }
    },
    handleEndMouseDown(e) {
      this.endDragging = true;
      this.origPos = e.clientX;
      this.origEnd = this.end;
      document.body.classList.add('unselectable');
      document.addEventListener('mousemove', this.handleEndMouseMove);
      document.addEventListener('mouseup', this.handleEndMouseUp);
    },
    handleEndMouseMove(e) {
      if (this.endDragging) {
        const delta = (e.clientX - this.origPos) / 672 * this.totalDuration;
        this.end = clamp(this.origEnd + delta, this.start + .4, this.totalDuration);
      }
    },
    handleEndMouseUp() {
      if (this.endDragging) {
        this.endDragging = false;
        document.removeEventListener('mousemove', this.handleEndMouseMove);
        document.removeEventListener('mouseup', this.handleEndMouseUp);
        setTimeout(() => {
          document.body.classList.remove('unselectable');
        }, 0);
      }
    },
    handleOK() {
      this.$emit('change', {
        start: this.start,
        end: this.end,
        total: this.totalDuration
      });
      this.close();
    },
    handleStartInputChange() {
      const start = clamp(this.displayStart, 0, this.totalDuration - .4);
      if (start > this.end - .4) {
        this.end = Math.min(start + .4, this.totalDuration);
      }
      this.start = start;
    },
    handleEndInputChange() {
      const end = clamp(this.displayEnd, .4, this.totalDuration);
      if (end < this.start + .4) {
        this.start = Math.max(end - .4, 0);
      }
      this.end = end;
    }
  },
  watch: {
    src() { },
    current(val) {
      if (!this.playing) {
        this.$refs.video.currentTime = val;
      }
    },
    visible(val) {
      if (val) {
        this.reset();
        this.$nextTick(() => {
          this.displayStart = this.start;
          this.displayEnd = this.end;
        });
      }
    },
    start(val) {
      this.displayStart = val;
    },
    end(val) {
      this.displayEnd = val;
    }
  },
  mounted() { }
}
</script>
<style>
.video-cropper-modal .ant-modal-body {
  background: #f7f8f9;
}

.video-cropper-modal__video {
  display: block;
  width: 100%;
  height: auto;
  max-height: 480px;
}

.video-cropper-modal__input {
  width: 80px;
  height: 28px;
  font-size: 100%;
  border-color: #dcdcde;
  border-radius: 2px;
}

.video-cropper-modal__input:before {
  content: "s";
  position: absolute;
  right: 6px;
  top: 0;
  line-height: 26px;
}

.video-cropper-modal__input .ant-input-number-input {
  height: 26px;
  line-height: 26px;
  padding-left: 8px;
}

.video-cropper-modal__btn {
  height: 28px;
  border-radius: 2px;
  line-height: 26px;
}

.video-cropper-container {
  overflow: hidden;
  padding: 0 20px;
  margin: 0 -20px 20px;
  position: relative;
}

.video-cropper-container:before {
  content: "";
  position: absolute;
  top: 0;
  left: 20px;
  right: 20px;
  bottom: 0;
  background: #fff;
}

.video-cropper-container__start,
.video-cropper-container__end {
  position: absolute;
  top: 4px;
}

.video-cropper-container__start {
  left: 28px;
}

.video-cropper-container__end {
  right: 28px;
}

.video-cropper-container__item {
  position: relative;
  border: 1px solid #e4e4e4;
  border-top: 0;
}

.video-cropper-container__body {
  height: 30px;
  background: rgba(0, 0, 0, 0.08);
  z-index: 0;
}
</style>